import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getEffectiveness, getEffectivenessLabel } from '../data/typeEffectiveness';
import WEATHER_BOOSTS, { WEATHER_OPTIONS, getWeatherBoost } from '../data/weatherBoosts';
import { calculateRaidDps } from '../utils/raidSimulator';
import { formatFormLabel } from '../utils/forms';
import { trackRaidBossSimulate } from '../utils/analytics';

const TYPE_COLORS = {
  Bug: '#A8B820', Dark: '#705848', Dragon: '#7038F8',
  Electric: '#F8D030', Fairy: '#EE99AC', Fighting: '#C03028',
  Fire: '#F08030', Flying: '#A890F0', Ghost: '#705898',
  Grass: '#78C850', Ground: '#E0C068', Ice: '#98D8D8',
  Normal: '#A8A878', Poison: '#A040A0', Psychic: '#F85888',
  Rock: '#B8A038', Steel: '#B8B8D0', Water: '#6890F0'
};

function getBaseName(pokemonName) {
  return pokemonName
    .replace(/\s*\(Mega\)/, '')
    .replace(/\s*\(Shadow\)/, '')
    .replace(/\s*\(Mega [XY]\)/, '')
    .trim();
}

function RaidBossChallenge({ allParties, allTierLists, onClose, initialBoss }) {
  const [pokemonData, setPokemonData] = useState(null);
  const [moveData, setMoveData] = useState(null);
  const [searchIndex, setSearchIndex] = useState([]);

  const [bossQuery, setBossQuery] = useState('');
  const [selectedBoss, setSelectedBoss] = useState(null);
  const [selectedBossForm, setSelectedBossForm] = useState(null);
  const [weather, setWeather] = useState('None');
  const [results, setResults] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const searchRef = useRef(null);
  const inputRef = useRef(null);

  // Load data on mount
  useEffect(() => {
    // pokemon_with_move_data.json contains Python NaN values which aren't valid JSON
    const fetchJsonWithNaN = (url) =>
      fetch(url).then(r => r.text()).then(t => JSON.parse(t.replace(/\bNaN\b/g, 'null')));

    Promise.all([
      fetch('/data/pokemon_data.json').then(r => r.json()),
      fetchJsonWithNaN('/data/pokemon_with_move_data.json'),
      fetch('/data/pokemon_search_index.json').then(r => r.json()),
    ]).then(([pd, md, si]) => {
      setPokemonData(pd);
      setMoveData(md);
      setSearchIndex(si);
    }).catch(err => console.error('Failed to load raid data:', err));
  }, []);

  // Build move lookup: moveName -> { power, duration, energy_delta, type }
  const moveLookup = useMemo(() => {
    if (!moveData) return { fast: {}, charged: {} };
    const fast = {};
    const charged = {};
    for (const p of moveData) {
      for (const m of (p.fast_moves || [])) {
        if (m.name && !fast[m.name]) fast[m.name] = m;
      }
      for (const m of (p.elite_fast_moves || [])) {
        if (m.name && !fast[m.name]) fast[m.name] = m;
      }
      for (const m of (p.charged_moves || [])) {
        if (m.name && !charged[m.name]) charged[m.name] = m;
      }
      for (const m of (p.elite_charged_moves || [])) {
        if (m.name && !charged[m.name]) charged[m.name] = m;
      }
    }
    return { fast, charged };
  }, [moveData]);

  // Search results for boss selector
  const searchResults = useMemo(() => {
    if (!bossQuery.trim() || !pokemonData) return [];
    const q = bossQuery.toLowerCase();
    return searchIndex
      .filter(p => p.name.toLowerCase().includes(q))
      .slice(0, 10);
  }, [bossQuery, searchIndex, pokemonData]);

  useEffect(() => { setHighlightIndex(-1); }, [searchResults]);

  // Click outside to close search
  useEffect(() => {
    function handleClickOutside(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsSearchOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectBoss = useCallback((name) => {
    if (!pokemonData || !pokemonData[name]) return;
    const data = pokemonData[name];
    setSelectedBoss({ name, ...data });
    // Default to "Normal" form, or first available form
    const defaultForm = data.form_stats?.Normal ? 'Normal' :
      (data.forms && data.forms.length > 0 ? data.forms[0] : null);
    setSelectedBossForm(defaultForm);
    setBossQuery(name);
    setIsSearchOpen(false);
    setResults(null);
  }, [pokemonData]);

  // Pre-select boss when initialBoss prop is provided
  useEffect(() => {
    if (initialBoss && pokemonData) {
      selectBoss(initialBoss);
    }
  }, [initialBoss, pokemonData, selectBoss]);

  const handleKeyDown = (e) => {
    if (!isSearchOpen || searchResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(i => Math.min(i + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      selectBoss(searchResults[highlightIndex].name);
    } else if (e.key === 'Escape') {
      setIsSearchOpen(false);
    }
  };

  // Get boss types based on selected form
  const bossTypes = useMemo(() => {
    if (!selectedBoss) return [];
    if (selectedBossForm && selectedBoss.form_stats?.[selectedBossForm]?.types) {
      return selectedBoss.form_stats[selectedBossForm].types;
    }
    return selectedBoss.types || [];
  }, [selectedBoss, selectedBossForm]);

  const bossDefense = useMemo(() => {
    if (!selectedBoss) return 150;
    if (selectedBossForm && selectedBoss.form_stats?.[selectedBossForm]?.base_defense) {
      return selectedBoss.form_stats[selectedBossForm].base_defense;
    }
    return selectedBoss.base_defense || 150;
  }, [selectedBoss, selectedBossForm]);

  // Available forms for selected boss (excluding Shadow since you fight the base form in raids)
  const bossFormOptions = useMemo(() => {
    if (!selectedBoss?.forms) return [];
    return selectedBoss.forms.filter(f => f !== 'Shadow');
  }, [selectedBoss]);

  // Categorize all 18 types by effectiveness against the boss
  const typeMatchups = useMemo(() => {
    if (bossTypes.length === 0) return null;
    const ALL_TYPES = Object.keys(TYPE_COLORS);
    const categories = { doubleSE: [], se: [], nve: [], doubleNVE: [] };
    for (const atkType of ALL_TYPES) {
      const eff = getEffectiveness(atkType, bossTypes);
      if (eff > 2) categories.doubleSE.push(atkType);
      else if (eff > 1.2) categories.se.push(atkType);
      else if (eff < 0.5) categories.doubleNVE.push(atkType);
      else if (eff < 0.9) categories.nve.push(atkType);
    }
    return categories;
  }, [bossTypes]);

  // Get weather-boosted types for the selected weather
  const weatherBoostedTypes = useMemo(() => {
    if (!weather || weather === 'None') return [];
    return WEATHER_BOOSTS[weather] || [];
  }, [weather]);

  // Collect all party Pokémon across all types
  const collectPartyPokemon = useCallback(() => {
    const seen = new Set();
    const collected = [];

    for (const [type, partyList] of Object.entries(allParties)) {
      if (!partyList || partyList.length === 0) continue;
      for (const p of partyList) {
        const id = `${p.pokemon_name}-${p.form}-${p.fast_move}-${p.charged_move}`;
        if (seen.has(id)) continue;
        seen.add(id);
        collected.push({ ...p, sourceType: type });
      }
    }

    return collected;
  }, [allParties]);

  // Look up base_attack + types for a party pokemon
  const lookupPokemonStats = useCallback((pokemon) => {
    if (!pokemonData || !moveData) return null;
    const baseName = getBaseName(pokemon.pokemon_name);
    const data = pokemonData[baseName];
    if (!data) return null;

    const isShadow = pokemon.pokemon_name.includes('(Shadow)');
    const isMega = pokemon.pokemon_name.includes('(Mega');

    // Determine form key for stat lookup
    let formKey = pokemon.form || 'Normal';
    if (isShadow) formKey = 'Shadow';
    if (isMega) {
      // Mega form names in form_stats: "Mega", "Mega_x", "Mega_y"
      if (pokemon.pokemon_name.includes('(Mega X)')) formKey = 'Mega_x';
      else if (pokemon.pokemon_name.includes('(Mega Y)')) formKey = 'Mega_y';
      else formKey = 'Mega';
    }

    const formStats = data.form_stats?.[formKey] || data.form_stats?.['Normal'] || {};
    const baseAttack = formStats.base_attack || data.base_attack;
    const types = formStats.types || data.types || [];

    return { baseAttack, types, isShadow, iconFilename: data.icon_filename };
  }, [pokemonData, moveData]);

  const runChallenge = useCallback(() => {
    if (!selectedBoss || !pokemonData || !moveData) return;

    const partyPokemon = collectPartyPokemon();
    if (partyPokemon.length === 0) {
      setResults([]);
      return;
    }

    const ranked = [];

    for (const p of partyPokemon) {
      const stats = lookupPokemonStats(p);
      if (!stats) continue;

      const fast = moveLookup.fast[p.fast_move];
      const charged = moveLookup.charged[p.charged_move];
      if (!fast || !charged) continue;

      const fastTypeEff = getEffectiveness(fast.type, bossTypes);
      const chargedTypeEff = getEffectiveness(charged.type, bossTypes);
      const fastWB = getWeatherBoost(weather, fast.type);
      const chargedWB = getWeatherBoost(weather, charged.type);

      const dps = calculateRaidDps({
        baseAttack: stats.baseAttack,
        pokemonTypes: stats.types,
        isShadow: stats.isShadow,
        fastMove: fast,
        chargedMove: charged,
        bossDefense,
        fastTypeEff,
        chargedTypeEff,
        fastWeatherBoost: fastWB,
        chargedWeatherBoost: chargedWB,
      });

      if (!dps) continue;

      ranked.push({
        ...p,
        iconFilename: stats.iconFilename,
        adjustedDpsL50: dps.dpsL50,
        fastType: fast.type,
        chargedType: charged.type,
        fastEff: fastTypeEff,
        chargedEff: chargedTypeEff,
        fastWeather: fastWB > 1,
        chargedWeather: chargedWB > 1,
      });
    }

    ranked.sort((a, b) => b.adjustedDpsL50 - a.adjustedDpsL50);
    if (ranked.length > 0 && selectedBoss?.name) {
      trackRaidBossSimulate(selectedBoss.name);
    }
    setResults(ranked);
  }, [selectedBoss, pokemonData, moveData, collectPartyPokemon, lookupPokemonStats, moveLookup, bossTypes, bossDefense, weather]);

  const isLoading = !pokemonData || !moveData;
  const totalPartyPokemon = collectPartyPokemon().length;

  const megaResults = useMemo(() => {
    if (!results) return [];
    return results.filter(p => p.pokemon_name.includes('(Mega')).slice(0, 3);
  }, [results]);

  const nonMegaResults = useMemo(() => {
    if (!results) return [];
    return results.filter(p => !p.pokemon_name.includes('(Mega'));
  }, [results]);

  const renderResultCard = (p, rank) => {
    const fastLabel = getEffectivenessLabel(p.fastEff);
    const chargedLabel = getEffectivenessLabel(p.chargedEff);

    return (
      <div
        key={`${p.pokemon_name}-${p.form}-${p.fast_move}-${p.charged_move}`}
        className="raid-result-card"
      >
        <div className="raid-result-rank">#{rank}</div>
        <div className="raid-result-icon-wrapper">
          {p.iconFilename && (
            <img
              src={`/pokemon_icons/${p.iconFilename}`}
              alt={p.pokemon_name}
              className="raid-result-icon"
              onError={e => { e.target.style.display = 'none'; }}
            />
          )}
        </div>
        <div className="raid-result-info">
          <div className="raid-result-name">{p.pokemon_name}</div>
          <div className="raid-result-moves">
            <span className="raid-move">
              {p.fast_move}
              {p.fastWeather && (
                <span className="raid-weather-badge" title="Weather boosted">☀</span>
              )}
              {fastLabel.label && (
                <span className={`raid-eff-badge ${fastLabel.className}`}>
                  {fastLabel.label}
                </span>
              )}
            </span>
            <span className="raid-move-sep">|</span>
            <span className="raid-move">
              {p.charged_move}
              {p.chargedWeather && (
                <span className="raid-weather-badge" title="Weather boosted">☀</span>
              )}
              {chargedLabel.label && (
                <span className={`raid-eff-badge ${chargedLabel.className}`}>
                  {chargedLabel.label}
                </span>
              )}
            </span>
          </div>
          <div className="raid-result-source">
            From:{' '}
            <span
              className="raid-source-type"
              style={{ color: TYPE_COLORS[p.sourceType] || '#ccc' }}
            >
              {p.sourceType}
            </span>{' '}
            party
          </div>
        </div>
        <div className="raid-result-dps">
          <div className="raid-dps-item">
            <span className="raid-dps-label">L50</span>
            <span className="raid-dps-value raid-dps-primary">
              {p.adjustedDpsL50}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal raid-modal" onClick={e => e.stopPropagation()}>
        <div className="raid-modal-header">
          <h2>Simulate Raid Boss</h2>
          <button className="raid-close-btn" onClick={onClose}>✕</button>
        </div>

        {isLoading ? (
          <div className="raid-loading">Loading Pokémon data...</div>
        ) : (
          <>
            <div className="raid-config">
              {/* Boss selector */}
              <div className="raid-field">
                <label>Raid Boss</label>
                <div className="raid-boss-search" ref={searchRef}>
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search for a Pokémon..."
                    value={bossQuery}
                    onChange={e => { setBossQuery(e.target.value); setIsSearchOpen(true); setSelectedBoss(null); setResults(null); }}
                    onFocus={() => { if (bossQuery.trim() && !selectedBoss) setIsSearchOpen(true); }}
                    onKeyDown={handleKeyDown}
                    className="raid-search-input"
                  />
                  {isSearchOpen && searchResults.length > 0 && (
                    <div className="raid-search-dropdown">
                      {searchResults.map((p, i) => (
                        <div
                          key={p.name}
                          className={`raid-search-result ${i === highlightIndex ? 'highlighted' : ''}`}
                          onMouseDown={() => selectBoss(p.name)}
                          onMouseEnter={() => setHighlightIndex(i)}
                        >
                          {p.icon_filename && (
                            <img
                              src={`/pokemon_icons/${p.icon_filename}`}
                              alt={p.name}
                              className="raid-search-icon"
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <span>{p.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Boss form selector */}
              {selectedBoss && bossFormOptions.length > 1 && (
                <div className="raid-field">
                  <label>Form</label>
                  <select
                    value={selectedBossForm || ''}
                    onChange={e => { setSelectedBossForm(e.target.value); setResults(null); }}
                    className="raid-select"
                  >
                    {bossFormOptions.map(f => (
                      <option key={f} value={f}>{formatFormLabel(f)}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Boss type display */}
              {selectedBoss && bossTypes.length > 0 && (
                <div className="raid-boss-types">
                  {bossTypes.map(t => (
                    <span key={t} className="raid-type-badge" style={{ backgroundColor: TYPE_COLORS[t] || '#888' }}>
                      {t}
                    </span>
                  ))}
                </div>
              )}

              {/* Type matchup summary */}
              {selectedBoss && typeMatchups && (
                <div className="raid-matchup-summary">
                  {typeMatchups.doubleSE.length > 0 && (
                    <div className="raid-matchup-row">
                      <span className="raid-matchup-label eff-double-se">2x Super Effective</span>
                      <div className="raid-matchup-types">
                        {typeMatchups.doubleSE.map(t => (
                          <span key={t} className="raid-type-mini" style={{ backgroundColor: TYPE_COLORS[t] }}>{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {typeMatchups.se.length > 0 && (
                    <div className="raid-matchup-row">
                      <span className="raid-matchup-label eff-se">Super Effective</span>
                      <div className="raid-matchup-types">
                        {typeMatchups.se.map(t => (
                          <span key={t} className="raid-type-mini" style={{ backgroundColor: TYPE_COLORS[t] }}>{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {typeMatchups.nve.length > 0 && (
                    <div className="raid-matchup-row">
                      <span className="raid-matchup-label eff-nve">Not Very Effective</span>
                      <div className="raid-matchup-types">
                        {typeMatchups.nve.map(t => (
                          <span key={t} className="raid-type-mini" style={{ backgroundColor: TYPE_COLORS[t] }}>{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {typeMatchups.doubleNVE.length > 0 && (
                    <div className="raid-matchup-row">
                      <span className="raid-matchup-label eff-double-nve">Immune</span>
                      <div className="raid-matchup-types">
                        {typeMatchups.doubleNVE.map(t => (
                          <span key={t} className="raid-type-mini" style={{ backgroundColor: TYPE_COLORS[t] }}>{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Weather selector */}
              <div className="raid-field">
                <label>Weather</label>
                <select
                  value={weather}
                  onChange={e => { setWeather(e.target.value); setResults(null); }}
                  className="raid-select"
                >
                  {WEATHER_OPTIONS.map(w => (
                    <option key={w} value={w}>{w}</option>
                  ))}
                </select>
              </div>

              {/* Weather-boosted types */}
              {weatherBoostedTypes.length > 0 && (
                <div className="raid-matchup-row raid-weather-row">
                  <span className="raid-matchup-label raid-weather-label">☀ Boosted</span>
                  <div className="raid-matchup-types">
                    {weatherBoostedTypes.map(t => (
                      <span key={t} className="raid-type-mini" style={{ backgroundColor: TYPE_COLORS[t] }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}

              <button
                className="button button-primary raid-challenge-btn"
                onClick={runChallenge}
                disabled={!selectedBoss}
              >
                ⚔️ Challenge!
              </button>

              {totalPartyPokemon === 0 && (
                <p className="raid-hint">
                  Add Pokémon to your parties across different type tier lists first, then come back to challenge a raid boss!
                </p>
              )}
            </div>

            {/* Results */}
            {results !== null && (
              <div className="raid-results">
                <h3>
                  {results.length > 0
                    ? `Your Top Counters vs ${selectedBoss.name}`
                    : 'No Counters Found'}
                </h3>
                {results.length === 0 ? (
                  <p className="raid-empty">
                    {totalPartyPokemon === 0
                      ? "You haven't added any Pokémon to your parties yet. Go to different type tier lists and build your parties first!"
                      : "None of your party Pokémon could be matched with move data. This might be a data issue."}
                  </p>
                ) : (
                  <div className="raid-results-list">
                    {megaResults.length > 0 && (
                      <>
                        <h4 className="raid-results-subheading">Top Mega Pokémon</h4>
                        {megaResults.map((p, index) => renderResultCard(p, index + 1))}
                      </>
                    )}
                    {nonMegaResults.length > 0 && (
                      <>
                        <h4 className="raid-results-subheading">Top Non-Mega Pokémon</h4>
                        {nonMegaResults.map((p, index) => renderResultCard(p, index + 1))}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default RaidBossChallenge;
